import { useAdminInsurerAttentionBulkCopy } from '@/hooks/admin';
import useAppToast from '@/hooks/useAppToast';
import { useTheme } from '@/shared/providers/theme';
import { AdminInsurerAttention } from '@/types/admin-insurer-attention';
import { convertWarekiDetail } from '@/utils/wareki';
import { AppButton } from '../atoms';

interface AdminInsurerAttentionReflectBoxProps {
  record: AdminInsurerAttention | null;
  selectedRowKeys: number[];
  onBulkCopySuccess?: () => void;
}

const formatWarekiDate = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  const era = convertWarekiDetail(y, m, d);
  if (!era) return '';
  return `${era}年${String(m).padStart(2, '0')}月${String(d).padStart(2, '0')}日`;
};

const AdminInsurerAttentionReflectBox = ({
  record,
  selectedRowKeys,
  onBulkCopySuccess,
}: AdminInsurerAttentionReflectBoxProps) => {
  const { mutate: bulkCopy, isPending } = useAdminInsurerAttentionBulkCopy(onBulkCopySuccess);
  const { colors } = useTheme();
  const toast = useAppToast();

  const handleBulkCopy = () => {
    if (!record) return;
    if (selectedRowKeys.length === 0) {
      toast.error('変更するデータを選択してください。');
      return;
    }
    bulkCopy({ source_id: record.id, target_ids: selectedRowKeys });
  };

  const attachments = record
    ? [
        record.attachment1,
        record.attachment2,
        record.attachment3,
        record.attachment4,
        record.attachment5,
      ].filter(Boolean)
    : [];

  return (
    <div className="rounded-md border overflow-hidden">
      {/* Header bar */}
      <div className="px-4 py-2" style={{ backgroundColor: colors.primary }}>
        <span className="text-white text-sm font-medium">コピー元情報</span>
      </div>

      {/* Body */}
      <div className="bg-white px-6 py-4 min-h-[80px]">
        {record ? (
          <div className="space-y-4">
            <div className="space-y-3">
              <div>
                <p className="text-sm font-bold">県No</p>
                <p className="text-sm">
                  {record.prefecture_id != null ? String(record.prefecture_id).padStart(2, '0') : ''}
                </p>
              </div>
              <div>
                <p className="text-sm font-bold">都道府県</p>
                <p className="text-sm">{record.prefecture_name ?? ''}</p>
              </div>
              <div>
                <p className="text-sm font-bold">掲載日</p>
                <p className="text-sm">{formatWarekiDate(record.publish_date)}</p>
              </div>
              <div>
                <p className="text-sm font-bold">注意事項</p>
                <div
                  className="text-sm"
                  dangerouslySetInnerHTML={{ __html: record.remarks ?? '' }}
                />
              </div>
              <div>
                <p className="text-sm font-bold">資料</p>
                {attachments.length > 0 ? (
                  <ul className="text-sm space-y-0.5">
                    {attachments.map((name, i) => (
                      <li key={i}>{name}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
            <AppButton
              type="primary"
              loading={isPending}
              disabled={isPending}
              onClick={handleBulkCopy}
            >
              一括コピー
            </AppButton>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AdminInsurerAttentionReflectBox;
